/**
 * Every decision `/tabelle` makes, and every mutation it APPLIES.
 *
 * The same split as lib/staffView.ts and lib/masterDataView.ts, for the same
 * reason (the STANDING RULE carried through the Task 7-10 reviews): pinning a
 * DECISION is not enough, because every escape those reviews found was planted
 * at a CALL SITE. So two layers live here:
 *
 *  1. THE DECISIONS: which state the binding is in, what a granted scope means
 *    in German, whether an identity was actually READ or merely hoped for, what
 *    a server error code says. Pure, exhaustively tested in Node
 *    (test/sheetView.test.ts).
 *  2. THE ORCHESTRATION: `*Action`-shaped functions taking their collaborators
 *    as parameters -- the API call, the Picker, the QueryClient, the state
 *    setters -- so the code that decides WHAT IS SENT and WHAT IS CLAIMED is
 *    callable from a Node test with no renderer.
 *
 * THE TWO RISKS THIS VIEW CARRIES, both security-shaped:
 *  - claiming a connection, or an ACCOUNT IDENTITY, that was never confirmed
 *    (plan 3c interface D13's second half -- see `googleIdentity`), and
 *  - letting the short-lived Picker access token outlive the one call it was
 *    minted for (risk A6). It is a parameter and a local `const` here, never a
 *    field, never returned, never stored -- and eslint.config.js bans the four
 *    spellings of client-side storage outright.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { t } from './i18n';
import { errorMessage } from '../components/ErrorNotice';
import { formatDateTime, splitScopes } from './startView';
import { invalidateMutation, reportAuthFailure } from '../queries';
import type { GoogleAccount } from './googleAccount';
import type { GoogleFinish, PickerToken, SheetDetail } from './types';

// ── error copy ───────────────────────────────────────────────────────────────

/**
 * The codes `/admin-api/google/*` and `/admin-api/sheet/*` actually answer
 * (api/src/adminRoutes.ts). Literal `t()` calls so test/i18nKeys.test.ts can
 * prove each string exists; anything else falls through to the shell's generic
 * `error.<code>` resolver.
 *
 * `template_not_configured` is deliberately ABSENT: it is a NAMED STATE, not
 * an error -- see `createSheetFromTemplateAction`.
 * `invalid_state` is absent too: it is the security screen
 * (lib/oauthLanding.ts's `finishFailureOutcome`), and reaching this resolver
 * with it would mean the landing flow had already gone wrong.
 */
export function sheetErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'google_not_connected': return t('error.google_not_connected');
      case 'google_token_failed': return t('error.google_token_failed');
      case 'no_sheet_connected': return t('error.no_sheet_connected');
      case 'invalid_spreadsheet_id': return t('error.invalid_spreadsheet_id');
      case 'template_copy_failed': return t('error.template_copy_failed');
      case 'exchange_failed': return t('error.exchange_failed');
    }
  }
  return errorMessage(error);
}

// ── the state of the binding ─────────────────────────────────────────────────

/**
 * `GET /admin-api/sheet` answers one row or null, and FOUR different screens
 * come out of it. The order of the tests below is the argument:
 *
 *  1. no row at all -> Google was never connected.
 *  2. `connectedVia === 'service_account'` -> SP-Onlinehandel's legacy binding
 *     (admin spec 4.5, 9): the operator manages it, so every connect/repair
 *     control is hidden rather than rendered and refused. Checked BEFORE the
 *     empty-id test, because a service-account row is not a half-finished
 *     OAuth connect and must never offer the Picker.
 *  3. `spreadsheetId === ''` -> the row `oauth-finish` writes BEFORE the
 *     Picker has run: Google is connected, no sheet is chosen yet.
 *  4. otherwise a real binding.
 */
export type SheetViewModel =
  | { kind: 'notConnected' }
  | { kind: 'serviceAccount'; detail: SheetDetailModel }
  | { kind: 'noSheet' }
  | { kind: 'connected'; detail: SheetDetailModel };

export interface SheetDetailModel {
  spreadsheetId: string;
  /** '' when there is no sheet id to link to (a service-account row is always
   *  expected to carry one, but the type does not promise it). */
  url: string;
  /** The full "Zuletzt geprüft: …" sentence, or null -- never a fallback date
   *  (the Task 8 review's mutation C, same class). */
  validatedLine: string | null;
  lastError: string | null;
  columns: ColumnRow[];
}

export interface ColumnRow { name: string; index: number }

export function spreadsheetUrl(spreadsheetId: string): string {
  if (!spreadsheetId) return '';
  return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(spreadsheetId)}/edit`;
}

/** The column map as a stable, sorted table: object key order is insertion
 *  order, and D1 round-trips it through JSON, so sorting by the COLUMN INDEX
 *  is what makes the table read like the sheet it describes. */
export function columnMapRows(columnMap: Record<string, number> | null): ColumnRow[] {
  if (!columnMap) return [];
  return Object.entries(columnMap)
    .map(([name, index]) => ({ name, index }))
    .sort((a, b) => a.index - b.index || a.name.localeCompare(b.name, 'de'));
}

function sheetDetailModel(sheet: SheetDetail): SheetDetailModel {
  return {
    spreadsheetId: sheet.spreadsheetId,
    url: spreadsheetUrl(sheet.spreadsheetId),
    validatedLine: sheet.validatedAt === null
      ? null
      : t('sheet.validatedAt', { date: formatDateTime(sheet.validatedAt) }),
    lastError: sheet.lastError,
    columns: columnMapRows(sheet.columnMap),
  };
}

export function sheetViewModel(sheet: SheetDetail | null): SheetViewModel {
  if (sheet === null) return { kind: 'notConnected' };
  if (sheet.connectedVia === 'service_account') return { kind: 'serviceAccount', detail: sheetDetailModel(sheet) };
  if (sheet.spreadsheetId === '') return { kind: 'noSheet' };
  return { kind: 'connected', detail: sheetDetailModel(sheet) };
}

// ── what was actually granted, and to whom ───────────────────────────────────

/**
 * The granted scope, in German. `drive.file` is the only scope this client
 * requests, and stating what it means -- access to the files you pick, and
 * nothing else in your Drive -- is the point of showing it at all. Anything
 * else is rendered VERBATIM: inventing a friendly name for a scope we did not
 * ask for would describe a grant nobody checked.
 */
export function scopeLabel(scope: string): string {
  switch (scope) {
    case 'https://www.googleapis.com/auth/drive.file': return t('sheet.scope.driveFile');
    default: return scope;
  }
}

export function scopeLabels(scope: string): string[] {
  return splitScopes(scope).map(scopeLabel);
}

/**
 * D13's other half: WHICH Google account is this connection bound to?
 *
 * THREE STATES, AND "unavailable" IS A REAL ANSWER. `fetchGoogleAccount`
 * returns null on any failure, and it can also return a 200 that named nobody
 * (`{}` with no `user`). BOTH are `unavailable`: rendering "verbunden als —"
 * or a blank line where an address belongs implies a check that did not
 * happen, and this whole panel exists because an operator must be able to see
 * a connection that landed in the wrong account. An honest "we could not read
 * this" is weaker than a name and far better than a wrong one.
 */
export type GoogleIdentityModel =
  | { kind: 'pending' }
  | { kind: 'unavailable'; note: string }
  | { kind: 'known'; email: string | null; displayName: string | null };

export function googleIdentity(account: GoogleAccount | null): GoogleIdentityModel {
  if (account === null) return { kind: 'unavailable', note: t('sheet.confirm.identityUnavailable') };
  const email = account.email?.trim() ?? '';
  const displayName = account.displayName?.trim() ?? '';
  if (email === '' && displayName === '') {
    return { kind: 'unavailable', note: t('sheet.confirm.identityUnavailable') };
  }
  return { kind: 'known', email: email === '' ? null : email, displayName: displayName === '' ? null : displayName };
}

// ── shared mutation shell ────────────────────────────────────────────────────

/** The three collaborators every mutation on this view needs, passed in and
 *  never reached for (lib/masterDataView.ts's `MutationShell`, copied). */
export interface MutationShell {
  queryClient: QueryClient;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
}

/** Busy on, previous error cleared, run, invalidate THROUGH THE SHARED MAP
 *  (queries.ts's `INVALIDATES`), report. Busy always ends false. */
export async function runSheetMutation<R>(
  mutation: string,
  run: () => Promise<R>,
  shell: MutationShell,
  onSuccess?: (result: R) => void,
): Promise<boolean> {
  shell.setBusy(true);
  shell.setError(null);
  try {
    const result = await run();
    invalidateMutation(shell.queryClient, mutation);
    onSuccess?.(result);
    return true;
  } catch (error) {
    // A 401 drives the SESSION, not just this panel's error line (I-1).
    reportAuthFailure(shell.queryClient, error);
    shell.setError(sheetErrorMessage(error));
    return false;
  } finally {
    shell.setBusy(false);
  }
}

// ── oauth-finish ─────────────────────────────────────────────────────────────

export interface FinishGoogleIO {
  queryClient: QueryClient;
  googleFinish: (state: string) => Promise<GoogleFinish>;
}

/**
 * The `onFinish` <ConnectOutcome> is given. It does exactly two things and
 * both are here rather than in an inline arrow at the JSX call site (the Task
 * 8 review's mutation B, same class): it POSTs the jti, and on success it
 * invalidates through the shared map, so the sheet card below the panel is not
 * still showing "nicht verbunden" after a successful connect.
 *
 * It deliberately does NOT catch: `runLandingFlow` classifies the failure, and
 * `400 invalid_state` must reach `finishFailureOutcome` as an ApiError to
 * become the security screen instead of a generic message.
 */
export async function finishGoogleConnect(state: string, io: FinishGoogleIO): Promise<GoogleFinish> {
  const result = await io.googleFinish(state);
  invalidateMutation(io.queryClient, 'googleFinish');
  return result;
}

// ── the identity read ────────────────────────────────────────────────────────

export interface GoogleIdentityIO {
  pickerToken: () => Promise<PickerToken>;
  readAccount: (accessToken: string) => Promise<GoogleAccount | null>;
  setIdentity: (identity: GoogleIdentityModel) => void;
}

/**
 * Mints a token, reads the account, and NEVER THROWS -- a failed identity read
 * is not a failed connect (the binding is already stored), so it degrades to
 * the honest "could not be read" line and leaves the rest of the confirmation
 * panel (scope, tenant, revocation link) standing.
 *
 * `accessToken` is a local `const` for the duration of one call: it is not
 * returned, not put in state, and not stored.
 */
export async function readGoogleIdentity(io: GoogleIdentityIO): Promise<void> {
  try {
    const { accessToken } = await io.pickerToken();
    io.setIdentity(googleIdentity(await io.readAccount(accessToken)));
  } catch {
    io.setIdentity(googleIdentity(null));
  }
}

// ── the Picker ───────────────────────────────────────────────────────────────

export interface PickSheetIO extends MutationShell {
  pickerToken: () => Promise<PickerToken>;
  /** lib/googlePicker.ts's `pickSpreadsheetId`, curried with its title at the
   *  call site. Resolves null when the operator closed the dialog. */
  pick: (accessToken: string) => Promise<string | null>;
  connectSheet: (spreadsheetId: string) => Promise<{ sheet: SheetDetail & { tabs: string[] } }>;
  onConnected: (tabs: string[]) => void;
}

/**
 * Mint -> pick -> connect, as one action, because the three only make sense
 * together and the token must not outlive them.
 *
 * A CLOSED DIALOG IS NOT A FAILURE: `pick` resolving null ends the action with
 * no request and no error message. Reporting "Der Vorgang ist fehlgeschlagen."
 * because somebody pressed Escape is how an operator learns to distrust the
 * error area.
 */
export async function pickAndConnectSheet(io: PickSheetIO): Promise<boolean> {
  io.setBusy(true);
  io.setError(null);
  let spreadsheetId: string | null;
  try {
    const { accessToken } = await io.pickerToken();
    spreadsheetId = await io.pick(accessToken);
  } catch (error) {
    // The picker-token mint is its own request and its own 401 (I-1): this
    // catch never reaches `runSheetMutation`, so it reports the session itself.
    reportAuthFailure(io.queryClient, error);
    io.setError(sheetErrorMessage(error));
    io.setBusy(false);
    return false;
  }
  if (spreadsheetId === null) {
    io.setBusy(false);
    return false;
  }
  return runSheetMutation(
    'connectSheet',
    () => io.connectSheet(spreadsheetId),
    io,
    (result) => io.onConnected(result.sheet.tabs),
  );
}

// ── validate / repair ────────────────────────────────────────────────────────

export interface ValidateSheetIO extends MutationShell {
  validateSheet: () => Promise<{ sheet: SheetDetail & { createdTabs: string[] } }>;
  onValidated: (createdTabs: string[]) => void;
}

export async function repairSheet(io: ValidateSheetIO): Promise<boolean> {
  return runSheetMutation(
    'validateSheet',
    () => io.validateSheet(),
    io,
    (result) => io.onValidated(result.sheet.createdTabs),
  );
}

// ── create from template ─────────────────────────────────────────────────────

export interface CreateFromTemplateIO extends MutationShell {
  createSheetFromTemplate: (json: { title?: string }) => Promise<{ sheet: SheetDetail }>;
  /** `503 template_not_configured` is a NAMED STATE, not an error toast. */
  setTemplateUnavailable: (unavailable: boolean) => void;
}

/**
 * "Neue Tabelle erstellen".
 *
 * `503 template_not_configured` is intercepted BEFORE the generic error path:
 * the StackStock template spreadsheet does not exist yet as an artifact (admin
 * spec 8, open point 3), so the endpoint ships behind its own config check and
 * this is the truthful state of the platform -- not a fault the owner caused
 * and not something "erneut versuchen" can fix. Everything else is an ordinary
 * failure.
 */
export async function createSheetFromTemplateAction(
  title: string,
  io: CreateFromTemplateIO,
): Promise<boolean> {
  const trimmed = title.trim();
  io.setTemplateUnavailable(false);
  io.setBusy(true);
  io.setError(null);
  try {
    await io.createSheetFromTemplate(trimmed === '' ? {} : { title: trimmed });
    invalidateMutation(io.queryClient, 'createSheetFromTemplate');
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'template_not_configured') {
      io.setTemplateUnavailable(true);
      return false;
    }
    // This action has its own try/catch (the 503 is a named state), so it also
    // needs its own session report -- it never passes through
    // `runSheetMutation` (I-1).
    reportAuthFailure(io.queryClient, error);
    io.setError(sheetErrorMessage(error));
    return false;
  } finally {
    io.setBusy(false);
  }
}
